import {
	OrderAdjustmentCalculationType,
	OrderAdjustmentType,
	useAddOrderQuoteOfferAdjustmentMutation,
	useUpdateOrderQuoteOfferAdjustmentMutation,
} from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { MarginInput } from '../../ui/margin-input';
import { cn } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';

/** The adjustment being amended, as the offer card holds it. */
export interface AdjustmentToEdit {
	id: string;
	name: string;
	description?: string | null;
	isFee: boolean;
	isPercentage: boolean;
	value: number;
}

/**
 * Authors an adjustment, or amends one that already exists.
 *
 * One modal for both, like `EditSampleModal`: the fields are the same and a
 * separate editor is how the two drift. What differs is that the API can only
 * update a name, a description and a value — an adjustment's KIND (fee vs
 * discount) and its BASIS (percentage vs fixed) have no field on
 * `UpdateOrderQuoteOfferAdjustmentInput`. Both controls are therefore shown but
 * locked while editing, rather than hidden: what a fee is remains worth seeing,
 * and a control that silently did nothing would be worse than one that says it
 * can't. Changing either means removing the adjustment and authoring it again.
 */
export function EditAdjustmentModal({
	open,
	onOpenChange,
	offerId,
	existing,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	offerId: string;
	/** Null when authoring a new one. */
	existing?: AdjustmentToEdit | null;
	onSuccess: () => void;
}) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [type, setType] = useState<'FEE' | 'DISCOUNT'>('FEE');
	const [calcType, setCalcType] = useState<'PERCENTAGE' | 'FIXED'>('PERCENTAGE');
	const [value, setValue] = useState('');
	const { selectedCurrency: currency } = useCurrencyStore();
	const [addAdjustment, { loading: adding }] = useAddOrderQuoteOfferAdjustmentMutation();
	const [updateAdjustment, { loading: updating }] = useUpdateOrderQuoteOfferAdjustmentMutation();
	const loading = adding || updating;
	const isEdit = !!existing;

	useEffect(() => {
		if (!open) return;
		if (existing) {
			setName(existing.name);
			setDescription(existing.description ?? '');
			setType(existing.isFee ? 'FEE' : 'DISCOUNT');
			setCalcType(existing.isPercentage ? 'PERCENTAGE' : 'FIXED');
			setValue(String(existing.value));
		} else {
			setName('');
			setDescription('');
			setType('FEE');
			setCalcType('PERCENTAGE');
			setValue('');
		}
	}, [open, existing]);

	const handleSubmit = async () => {
		if (!name.trim() || !value) return;
		try {
			if (existing) {
				await updateAdjustment({
					variables: {
						id: existing.id,
						input: {
							name: name.trim(),
							// Sent even when empty, so clearing a description reaches
							// the API instead of leaving the old one in place.
							description: description.trim(),
							value: parseFloat(value),
							// Only meaningful on a fixed adjustment; a percentage has no
							// currency to restate.
							currency: calcType === 'FIXED' ? currency : undefined,
						},
					},
				});
				toast.success('Adjustment updated');
			} else {
				await addAdjustment({
					variables: {
						input: {
							orderQuoteOfferId: offerId,
							name: name.trim(),
							description: description.trim() || undefined,
							type:
								type === 'FEE'
									? OrderAdjustmentType.Fee
									: OrderAdjustmentType.Discount,
							calculationType:
								calcType === 'PERCENTAGE'
									? OrderAdjustmentCalculationType.Percentage
									: OrderAdjustmentCalculationType.Fixed,
							value: parseFloat(value),
							currency: calcType === 'FIXED' ? currency : undefined,
						},
					},
				});
				toast.success('Adjustment added');
			}
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save adjustment');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>{isEdit ? 'Edit adjustment' : 'Add adjustment'}</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label>Type</Label>
								<div className="flex gap-1">
									<button
										type="button"
										disabled={isEdit}
										onClick={() => setType('FEE')}
										className={cn(
											'flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
											type === 'FEE'
												? 'border-status-warn bg-status-warn/10 text-status-warn'
												: 'border-line-2 text-text-3',
											isEdit
												? 'cursor-default'
												: type !== 'FEE' && 'hover:border-line-3'
										)}
									>
										Fee
									</button>
									<button
										type="button"
										disabled={isEdit}
										onClick={() => setType('DISCOUNT')}
										className={cn(
											'flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
											type === 'DISCOUNT'
												? 'border-status-success bg-status-success/10 text-status-success'
												: 'border-line-2 text-text-3',
											isEdit
												? 'cursor-default'
												: type !== 'DISCOUNT' && 'hover:border-line-3'
										)}
									>
										Discount
									</button>
								</div>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Setup fee, Volume discount"
								/>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Description</Label>
								<Input
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Optional"
								/>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>
									Value <span className="text-destructive">*</span>
								</Label>
								<MarginInput
									value={value}
									onChange={setValue}
									mode={calcType === 'PERCENTAGE' ? 'percentage' : 'fixed'}
									currency={currency}
									onModeChange={
										isEdit
											? undefined
											: (m) =>
													setCalcType(
														m === 'percentage' ? 'PERCENTAGE' : 'FIXED'
													)
									}
								/>
								{isEdit && (
									<span className="text-2xs leading-snug text-text-4">
										The amount can change; whether this is a fee or a discount,
										and percentage or fixed, cannot. Remove it and add it again
										to change those.
									</span>
								)}
							</div>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || !value || loading}>
							{loading ? 'Saving…' : isEdit ? 'Save' : 'Add'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
